import { useDebounce } from "@/app/hooks/useDebounce";
import { useGetAllUserLinks } from "@/app/hooks/useGetAllUserLinks";
import { selfHireService } from "@/app/services/selfHireService";
import { useToast } from "@/view/components/ui/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";

export const useLinksController = () => {
    const [searchValue, setSearchValue] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLink, setSelectedLink] = useState({ name: '' });
    const [isCreateLinkModalOpen, setIsCreateLinkModalOpen] = useState(false);
    const [newLinkName, setNewLinkName] = useState('');
    const [selectedTable, setSelectedTable] = useState('');
    const queryClient = useQueryClient();
    const debouncedSearchValue = useDebounce(500, searchValue);
    const navigate = useNavigate();
    
    const { ref, inView } = useInView();

    const { toast } = useToast();

    const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    };

    const {
        data,
        isFetching,
        setParams,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
      } = useGetAllUserLinks();


      const setSearch = useCallback(() => {
        setParams((state) => ({ ...state, search: debouncedSearchValue }));
      }, [debouncedSearchValue, setParams]);

      const { mutate: createLinkMutation, isPending } = useMutation({
        mutationFn: async ({
          name,
          tableId,
        }: {
          name: string;
          tableId: string;
        }) => {
          return await selfHireService.createLink({ name, tableId });
        },
        onSuccess: (variables) => {
          queryClient.invalidateQueries({ queryKey: ['AllUserLinks'] });
    
          setIsCreateLinkModalOpen(false);
    
          setNewLinkName("");
          setSelectedTable("");

          setSelectedLink({ name: variables.name });
          setIsModalOpen(true);
    
          toast({
            variant: 'default',
            title: 'Link criado com sucesso!',
          });
        },
        onError: (error: any) => {
          toast({
            variant: 'destructive',
            title: error?.message || "Não foi possível criar o link, tente novamente",
          })
        },
      });
    
      const handleOpenModal = (link: { name: string }) => {
        setSelectedLink({ name: link.name });
        setIsModalOpen(true);
      };
    
      const handleCloseModal = () => {
        setIsModalOpen(false);
      };
    
      const handleCreateLink = () => {
        if (newLinkName && selectedTable) {
          createLinkMutation({
            name: newLinkName.trim(),
            tableId: selectedTable,
          });
        }
      };

      const handleLinkClick = (linkId: string) => {
        navigate(`/links/${linkId}`);
      };
    
      useEffect(() => {
        setSearch();
      }, [setSearch]);

      useEffect(() => {
        if (inView) {
          fetchNextPage();
        }
      }, [fetchNextPage, inView]);

      return {
        data,
        isFetching,
        hasNextPage,
        isFetchingNextPage,
        handleSearchInputChange,
        ref,
        searchValue,
        isModalOpen,
        selectedLink,
        isCreateLinkModalOpen,
        newLinkName,
        selectedTable,
        setIsCreateLinkModalOpen,
        setNewLinkName,
        setSelectedTable,
        handleOpenModal,
        handleCloseModal,
        handleCreateLink,
        isPending,
        handleLinkClick,
      }
}